import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MapMouseEvent, Map as MapLibreMap, Marker } from 'maplibre-gl'
import { Box, Button, Typography } from '@mui/material'
import { ChevronLeft, ChevronRight, CircleX, ScissorsLineDashed, TriangleAlert, X } from 'lucide-react'
import { inspectQuery, useNavigate } from '../../lib/router'
import type { Run } from '../../api/types'
import {
  useIssues, usePlot, usePlotClusters, usePlotsGeoJson, useReferenceLayers, useTrimPlot,
} from '../../api/hooks'
import { ApiError, get } from '../../api/client'
import { BasemapSwitch, MapCanvas, MapControl, ZoomControl } from '../../components/MapCanvas'
import { Mono, StatusPill } from '../../components/primitives'
import { ha, num } from '../../lib/format'
import { STATUS_COLOURS, tokens } from '../../theme'
import {
  addPlotLayers, PLOTS_FILL, setBoundary, setBoundaryVisible, setPlots, setSelected,
} from './PlotsLayer'
import {
  addClusterLayers, CLUSTER_MAX_ZOOM, removeClusterMarkers, syncCells,
} from './PlotClusters'

interface Props {
  run: Run
  /** Lifted to the route so the map and the table share one filter. */
  checkFilter: string[]
  onCheckFilter: (keys: string[]) => void
}

interface Popover {
  id: string
  plotRef: string | null
  status: keyof typeof STATUS_COLOURS
  areaHa: number | null
  farmerId: string | null
  yieldKg: number | null
  /** Anchored to the ground, so panning and zooming keep it on its plot. */
  lngLat: [number, number]
}

/** Screen 1b — map first, with the checks acting as filters. */
export function ResultsMap({ run, checkFilter, onCheckFilter }: Props) {
  const navigate = useNavigate()
  const mapRef = useRef<MapLibreMap | null>(null)
  const [ready, setReady] = useState(false)
  const [basemap, setBasemap] = useState<'map' | 'satellite'>('map')
  const [showPassing, setShowPassing] = useState(true)
  const [popover, setPopover] = useState<Popover | null>(null)

  const activeChecks = checkFilter
  const summary = run.summary

  const statusFilter = showPassing ? undefined : 'flagged,rejected,excluded'
  const filters = { check: activeChecks.join(',') || undefined, status: statusFilter }

  /**
   * What is on screen: the zoom, and the area a little wider than the
   * screen, rounded to the grid the server groups by so that a small pan
   * asks the same question again (and gets it from cache).
   */
  const [view, setView] = useState<{ bbox: string; zoom: number } | null>(null)
  // Nothing is asked for the area on screen until the map has been framed on
  // the region: before that it shows wherever the map happened to start.
  const [framed, setFramed] = useState(false)

  /**
   * The zoom decides, whatever the size of the region: far out, plots are
   * grouped by the server into bubbles; from CLUSTER_MAX_ZOOM, where a field
   * has a readable shape, they are outlines for the area on screen. Neither
   * answer grows with the region, so none nears what a response may carry.
   */
  const close = framed && Boolean(view) && view!.zoom >= CLUSTER_MAX_ZOOM
  const area = usePlotsGeoJson(
    run.id,
    { ...filters, simplify: '0.00001', bbox: view?.bbox },
    { enabled: close, keepPrevious: true },
  )
  // Close in but still more plots on screen than one response carries (a
  // dense estate): bubbles a while longer rather than an error.
  const tooDense = close && area.error instanceof ApiError && area.error.status === 422
  // Bubbles or outlines — the legend has to say which it is showing.
  const clustered = !close || tooDense

  // The whole region, as one or two cells: its count, and where to frame.
  const { data: frame } = usePlotClusters(run.id, { ...filters, zoom: '0' })
  // The bubbles on screen.
  const { data: cellData } = usePlotClusters(
    run.id,
    { ...filters, zoom: String(Math.floor(view?.zoom ?? 0)), bbox: view?.bbox },
    { enabled: framed && Boolean(view) && clustered, keepPrevious: true },
  )

  const plots = clustered ? undefined : area.data
  const cells = clustered ? cellData?.cells ?? [] : []
  const total = frame?.total
  const plotsError = tooDense ? null : area.error
  const outlinesLoading = close && area.isFetching

  // The failures behind the active filter, in file order, so they can be
  // stepped through one at a time: spread across a country they are otherwise
  // impossible to find on the map.
  const { data: issueData } = useIssues(activeChecks.length ? run.id : undefined, {
    check: activeChecks.join(','),
    limit: '1000',
  })
  const issueList = useMemo(() => {
    const seen = new Set<string>()
    return (issueData?.issues ?? []).filter((i) =>
      seen.has(i.plot_uuid) ? false : (seen.add(i.plot_uuid), true),
    )
  }, [issueData])
  const [stepIndex, setStepIndex] = useState(-1)
  useEffect(() => setStepIndex(-1), [activeChecks.join(',')])

  const stepTo = useCallback(
    (index: number) => {
      const target = issueList[index]
      const map = mapRef.current
      if (!target?.centroid || !map) return
      const [lng, lat] = target.centroid.coordinates
      setStepIndex(index)
      map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 15), duration: 600 })
      setPopover({
        id: target.plot_uuid,
        plotRef: target.plot_ref,
        status: target.status,
        areaHa: target.area_ha,
        farmerId: target.farmer_id,
        yieldKg: null,
        lngLat: [lng, lat],
      })
    },
    [issueList],
  )

  const { data: layers = [] } = useReferenceLayers()
  const boundaryLayerId = run.config?.layers?.country_boundary ?? layers.find((l) => l.kind === 'country_boundary' && l.is_default)?.id

  // The dashed country outline in the 1b legend: the country the run is
  // judged against. Only that one — the whole world layer is about 13 MB,
  // twice what the deployed API can return, so asking for it drew nothing.
  const [boundary, setBoundaryData] = useState<GeoJSON.FeatureCollection | null>(null)
  const boundaryCountry = run.country_code
  useEffect(() => {
    if (!boundaryLayerId || !boundaryCountry) return
    let cancelled = false
    get<GeoJSON.FeatureCollection>(
      `/reference-layers/${boundaryLayerId}/features.geojson?country=${encodeURIComponent(boundaryCountry)}`,
    )
      .then((fc) => !cancelled && setBoundaryData(fc))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [boundaryLayerId, boundaryCountry])

  // The clicked plot's own issues, for the sentence and the fix button.
  const { data: popoverDetail } = usePlot(popover?.id, run.id)
  const issueForPopover = popoverDetail?.issues[0]

  const trim = useTrimPlot(run.id)

  const onReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map
    addPlotLayers(map)
    addClusterLayers(map)
    map.on('mouseenter', PLOTS_FILL, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', PLOTS_FILL, () => {
      map.getCanvas().style.cursor = ''
    })
    setReady(true)
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (map && ready) setPlots(map, plots)
  }, [plots, ready])

  // Framed on the whole region when it opens and when the filters change;
  // panning, which only changes what is fetched, never reframes.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    if (!frame) return
    const b = frame.extent
    if (!b) {
      setFramed(true)
      return
    }
    map.once('moveend', () => setFramed(true))
    // A frame that does not move the map may not end with a moveend.
    const fallback = window.setTimeout(() => setFramed(true), 800)
    map.fitBounds([[b[0], b[1]], [b[2], b[3]]], {
      padding: { top: 64, bottom: 64, left: 64, right: 64 }, maxZoom: 17, duration: 400,
    })
    return () => window.clearTimeout(fallback)
  }, [frame, ready])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    const update = () => {
      const zoom = map.getZoom()
      const b = map.getBounds()
      const padX = (b.getEast() - b.getWest()) * 0.25
      const padY = (b.getNorth() - b.getSouth()) * 0.25
      // The server's cell at this zoom (64 px), or a finer step for outlines.
      const step = zoom < CLUSTER_MAX_ZOOM ? 90 / 2 ** Math.floor(zoom) : 0.002
      const down = (v: number) => Math.floor(v / step) * step
      const up = (v: number) => Math.ceil(v / step) * step
      const fix = (v: number) => Number(v.toFixed(6))
      setView({
        bbox: [down(b.getWest() - padX), down(b.getSouth() - padY), up(b.getEast() + padX), up(b.getNorth() + padY)]
          .map((v) => fix(Math.max(-180, Math.min(180, v)))).join(','),
        zoom,
      })
    }
    update()
    map.on('moveend', update)
    return () => {
      map.off('moveend', update)
    }
  }, [ready])

  /** The server's cells on the map: dots for lone plots, bubbles for groups. */
  const markers = useRef<Map<string, Marker>>(new Map())
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    syncCells(map, markers.current, clustered ? cells : [], (lngLat) =>
      // A step in, as far as the outlines at most: the next answer splits it.
      map.easeTo({
        center: lngLat,
        // Not past where the outlines start, unless the outlines are what is
        // too dense here.
        zoom: map.getZoom() < CLUSTER_MAX_ZOOM ? Math.min(map.getZoom() + 2, CLUSTER_MAX_ZOOM) : map.getZoom() + 2,
        duration: 500,
      }))
  }, [ready, cells, clustered])
  useEffect(() => {
    const held = markers.current
    return () => removeClusterMarkers(held)
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (map && ready && boundary) setBoundary(map, boundary)
  }, [boundary, ready])

  // Hidden over the street basemap, which draws its own borders: two lines a
  // little apart look like a disagreement about where the border is.
  useEffect(() => {
    const map = mapRef.current
    if (map && ready) setBoundaryVisible(map, basemap === 'satellite')
  }, [basemap, ready, boundary])

  useEffect(() => {
    const map = mapRef.current
    if (map && ready) setSelected(map, popover?.id ?? null)
  }, [popover, ready])

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const map = mapRef.current
      if (!map) return
      const hit = map.queryRenderedFeatures(event.point, { layers: [PLOTS_FILL] })[0]
      if (!hit) {
        setPopover(null)
        return
      }
      const p = hit.properties as Record<string, any>
      setPopover({
        id: String(p.id),
        plotRef: p.plot_ref ?? null,
        status: (p.status ?? 'passed') as Popover['status'],
        areaHa: p.area_ha ?? null,
        farmerId: p.farmer_id ?? null,
        yieldKg: p.yield_kg ?? null,
        lngLat: [event.lngLat.lng, event.lngLat.lat],
      })
      setStepIndex(issueList.findIndex((i) => i.plot_uuid === String(p.id)))
    },
    // `issueList` is read through a ref-like closure refreshed on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [issueList],
  )

  // Screen position of the popover, recomputed as the map moves.
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const map = mapRef.current
    if (!map || !popover) {
      setAnchor(null)
      return
    }
    const place = () => {
      const point = map.project(popover.lngLat)
      setAnchor({ x: point.x, y: point.y })
    }
    place()
    map.on('move', place)
    return () => {
      map.off('move', place)
    }
  }, [popover])

  const toggleCheck = (key: string) =>
    onCheckFilter(activeChecks.includes(key) ? activeChecks.filter((k) => k !== key) : [...activeChecks, key])

  return (
    <MapCanvas onReady={onReady} onMapClick={handleClick} basemap={basemap}>
      {/* The API refuses more plots than one response can carry, and says
          why; the map has nothing to draw, so it says so too. */}
      {/* Only when there is something to say: the outlines are on their way,
          or there are too many here to draw. That the map is grouping is the
          legend's job, and a note saying so stayed on screen after it no
          longer helped. */}
      {total !== undefined && total > 0 && !plotsError && (tooDense || outlinesLoading) && (
        <Box
          role="status"
          sx={{
            position: 'absolute', bottom: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
            px: 3, py: 1.5, borderRadius: tokens.radius.pill,
            backgroundColor: 'background.paper', boxShadow: tokens.shadow[1],
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {tooDense
              ? 'Too many plots here to outline at once · zoom in further'
              : 'Loading the outlines here…'}
          </Typography>
        </Box>
      )}
      {plotsError && (
        <Box
          role="status"
          sx={{
            position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
            maxWidth: 520, px: 4, py: 3, borderRadius: `${tokens.radius.md}px`,
            backgroundColor: 'background.paper', boxShadow: tokens.shadow[2],
            display: 'flex', gap: 2.5, alignItems: 'flex-start',
          }}
        >
          <TriangleAlert size={16} strokeWidth={1.5} style={{ flex: 'none', marginTop: 2 }} />
          <Typography variant="body2">
            {plotsError instanceof ApiError && plotsError.status === 422
              ? `${num((plotsError.body as { plots?: number })?.plots ?? 0)} plots in view are too many to outline at once. ` +
                'Zoom in further, or narrow the map with the check filters on the left.'
              : (plotsError as Error).message}
          </Typography>
        </Box>
      )}
      {/* Filter chips */}
      <Box sx={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 1.5, flexWrap: 'wrap', maxWidth: 'calc(100% - 200px)' }}>
        {activeChecks.map((key) => {
          const check = summary?.checks[key]
          return (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => toggleCheck(key)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1.5, height: 28, px: 2.5, border: 0,
                borderRadius: tokens.radius.pill, cursor: 'pointer',
                backgroundColor: tokens.color.brandDeep, color: tokens.color.ink0,
                font: `500 12px ${tokens.font.sans}`,
              }}
            >
              {check?.label ?? key}
              <X size={12} strokeWidth={2} />
            </Box>
          )
        })}
        <Box
          component="button"
          type="button"
          aria-pressed={showPassing}
          onClick={() => setShowPassing((v) => !v)}
          sx={{
            display: 'inline-flex', alignItems: 'center', height: 28, px: 2.5, cursor: 'pointer',
            borderRadius: tokens.radius.pill, font: `500 12px ${tokens.font.sans}`,
            backgroundColor: showPassing ? 'background.paper' : tokens.color.ink700,
            border: `1px solid ${showPassing ? tokens.color.ink200 : tokens.color.ink700}`,
            color: showPassing ? 'text.secondary' : tokens.color.ink0,
          }}
        >
          Show passing
        </Box>
      </Box>

      {/* Step through the failures of the filtered check. */}
      {issueList.length > 0 && (
        <MapControl
          sx={{
            top: 16, left: '50%', transform: 'translateX(-50%)',
            alignItems: 'center', height: 32,
          }}
        >
          <StepButton
            label="Previous failing polygon"
            onClick={() => stepTo(stepIndex <= 0 ? issueList.length - 1 : stepIndex - 1)}
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </StepButton>
          <Typography
            sx={{
              px: 3, font: `500 12px ${tokens.font.sans}`, whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums', borderLeft: `1px solid ${tokens.color.ink200}`,
              borderRight: `1px solid ${tokens.color.ink200}`, lineHeight: '32px',
            }}
          >
            {stepIndex >= 0 ? `${num(stepIndex + 1)} of ${num(issueList.length)}` : `${num(issueList.length)} to review`}
          </Typography>
          <StepButton
            label="Next failing polygon"
            onClick={() => stepTo(stepIndex + 1 >= issueList.length ? 0 : stepIndex + 1)}
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </StepButton>
        </MapControl>
      )}

      <ZoomControl map={mapRef.current} sx={{ top: 64 }} />
      <BasemapSwitch
        value={basemap}
        onChange={setBasemap}
        options={[{ value: 'map', label: 'Basemap' }, { value: 'satellite', label: 'Satellite' }]}
      />

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute', left: 16, bottom: 16, display: 'flex', gap: 3.5, alignItems: 'center',
          px: 3, py: 2, backgroundColor: 'background.paper', border: `1px solid ${tokens.color.ink200}`,
          borderRadius: `${tokens.radius.md}px`, font: `400 12px ${tokens.font.sans}`, color: 'text.secondary',
        }}
      >
        <Swatch colour={STATUS_COLOURS.passed}>Passed</Swatch>
        <Swatch colour={STATUS_COLOURS.flagged}>Flagged</Swatch>
        <Swatch colour={STATUS_COLOURS.rejected}>Rejected</Swatch>
        {/* Otherwise a bubble reads as one big plot rather than many small
            ones, and its colour as a verdict on all of them. */}
        {clustered && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} data-testid="cluster-legend">
            <Box
              sx={{
                width: 15, height: 15, borderRadius: '50%', flex: 'none',
                backgroundColor: tokens.color.ink400, border: `2px solid ${tokens.color.ink0}`,
                boxShadow: tokens.shadow[1],
              }}
            />
            Grouped — zoom in for outlines
          </Box>
        )}
        {basemap === 'satellite' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 16, borderTop: `1.5px dashed ${tokens.color.ink500}` }} />
            Country boundary
          </Box>
        )}
      </Box>

      {/* Plot popover */}
      {popover && anchor && (
        <Box
          sx={{
            position: 'absolute',
            left: anchor.x + 12,
            top: anchor.y + 12,
            width: 300,
            backgroundColor: 'background.paper',
            border: `1px solid ${tokens.color.ink200}`,
            borderRadius: `${tokens.radius.lg}px`,
            boxShadow: tokens.shadow[3],
            overflow: 'hidden',
            zIndex: 5,
          }}
        >
          <Box sx={{ px: 4, py: 3.5, borderBottom: `1px solid ${tokens.color.ink200}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Mono sx={{ fontSize: 13, fontWeight: 500 }}>{popover.plotRef ?? '—'}</Mono>
              <StatusPill status={popover.status} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              {popover.farmerId ? `Farmer ${popover.farmerId} · ` : ''}
              {ha(popover.areaHa)}
              {popover.yieldKg ? ` · ${num(popover.yieldKg)} kg` : ''}
            </Typography>
          </Box>

          {issueForPopover && (
            <Box sx={{ px: 4, py: 3, display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
              <Box sx={{ color: issueForPopover.tier === 'compliance' ? tokens.color.danger : tokens.color.warning, mt: 0.25, flex: 'none' }}>
                {issueForPopover.tier === 'compliance' ? <CircleX size={16} strokeWidth={1.5} /> : <TriangleAlert size={16} strokeWidth={1.5} />}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {issueForPopover.message}
              </Typography>
            </Box>
          )}

          <Box sx={{ px: 4, pb: 3.5, display: 'flex', gap: 2 }}>
            <Button size="small" onClick={() => navigate(`/checks/runs/${run.id}/plots/${popover.id}${inspectQuery(checkFilter)}`)}>
              Inspect
            </Button>
            {issueForPopover?.check_key === 'within_country' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ScissorsLineDashed size={14} strokeWidth={1.5} />}
                disabled={trim.isPending}
                onClick={() => trim.mutate({ plotId: popover.id, against: 'country' })}
              >
                Clip to border
              </Button>
            )}
            {issueForPopover?.check_key === 'geometry_errors' && (
              <Button size="small" variant="outlined" onClick={() => navigate(`/checks/runs/${run.id}/plots/${popover.id}${inspectQuery(checkFilter)}`)}>
                Repair
              </Button>
            )}
          </Box>
        </Box>
      )}
    </MapCanvas>
)
}


function Swatch({ colour, children }: { colour: (typeof STATUS_COLOURS)[keyof typeof STATUS_COLOURS]; children: React.ReactNode }) {
return (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: `${tokens.radius.xs}px`, backgroundColor: colour.fill, border: `1px solid ${colour.stroke}` }} />
    {children}
  </Box>
)
}



function StepButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
return (
  <Box
    component="button"
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    sx={{
      width: 32, height: 32, border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
      backgroundColor: 'transparent', color: 'text.secondary',
      '&:hover': { backgroundColor: tokens.color.ink100 },
    }}
  >
    {children}
  </Box>
)
}
